import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { getCookies } from '../../lib/getCookies';
import { useCountry } from '../../components/CountrySelector';
import { useCart } from '../useCart';
import { useUser } from '../useUser';
import { useCookies } from '../useCookies';
import { useSignifydSessionId } from '../useSignifydSessionId';
import { GET_REWARDS_SUMMARY_QUERY } from '../useRewardsSummary';
import { getGA4Ids } from '../../lib/googleAnalytics/ga4/getGa4Ids';
import { logGa4IdsDiagnostics } from '../../lib/googleAnalytics/ga4/logGa4IdsDiagnostics';
import { PLACE_ORDER_MUTATION, PlaceOrderMutationInput } from './placeOrderMutation.rest';

export const usePlaceOrderMutation = () => {
  const {
    cookies: { modaAnonymousId }
  } = useCookies();
  const { country } = useCountry();
  const { signifydSessionId, clearSignifydSessionId } = useSignifydSessionId();
  const {
    cart: {
      id: cartId,
      attributes: {
        addressId,
        giftCode,
        paymentMethodId,
        selectedShippingMethodId,
        paymentSource,
        error: cartError
      }
    }
  } = useCart();
  const hasInvalidCoupon = cartError?.type.name === 'Invalid Coupon';
  const {
    user: { isLoggedIn }
  } = useUser();

  const [mutate, { loading, error }] = useMutation(PLACE_ORDER_MUTATION, {
    refetchQueries: isLoggedIn
      ? [
          {
            query: GET_REWARDS_SUMMARY_QUERY,
            variables: {
              countryCode: country.alpha2Code,
              currency: country.currencyCode
            }
          }
        ]
      : []
  });

  const getOrderAttributes = useCallback(
    async (sameDayDeliveryNotes?: string) => {
      if (!addressId) throw new Error('placeOrderMutation: no address selected');
      if (!cartId) throw new Error('placeOrderMutation: cart id not found');
      if (!paymentMethodId) throw new Error('placeOrderMutation: no payment method selected');
      if (!selectedShippingMethodId)
        throw new Error('placeOrderMutation: no shipping method selected');
      if (!paymentSource) throw new Error('placeOrderMutation: payment source not found');

      const cookies = getCookies();

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { ga4_client_id, ga4_session_id, ga4_session_number, diagnostics } = await getGA4Ids();
      logGa4IdsDiagnostics({ checkoutFlow: 'standard', cartId, ...diagnostics });

      return {
        address_id: addressId,
        cart_id: cartId,
        country_code: country.alpha2Code,
        event_source: 'astrarium',
        offer_code: !hasInvalidCoupon ? giftCode : null,
        order_session_id: signifydSessionId,
        payment_id: paymentMethodId,
        payment_source: paymentSource,
        shipment_method: selectedShippingMethodId,
        linkshare: cookies.linkshare,
        ga4_client_id,
        ga4_session_id,
        ga4_session_number,
        segment_id: modaAnonymousId ?? undefined,
        same_day_delivery_notes: sameDayDeliveryNotes || undefined
      };
    },
    [
      cartId,
      addressId,
      giftCode,
      paymentMethodId,
      selectedShippingMethodId,
      paymentSource,
      country,
      signifydSessionId,
      modaAnonymousId,
      hasInvalidCoupon
    ]
  );

  const handlePlaceOrder = useCallback(
    async (
      attributes?: Omit<
        PlaceOrderMutationInput['data']['attributes'],
        'order_session_id' | 'event_source'
      >,
      sameDayDeliveryNotes?: string
    ) => {
      let resolvedAttributes;
      if (attributes) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { ga4_client_id, ga4_session_id, ga4_session_number, diagnostics } =
          await getGA4Ids();
        logGa4IdsDiagnostics({ checkoutFlow: 'standard', cartId, ...diagnostics });
        resolvedAttributes = {
          ga4_client_id,
          ga4_session_id,
          ga4_session_number,
          ...attributes,
          order_session_id: signifydSessionId,
          event_source: 'astrarium'
        };
      } else {
        resolvedAttributes = await getOrderAttributes(sameDayDeliveryNotes);
      }

      const result = await mutate({
        variables: {
          input: {
            data: {
              type: 'orders',
              attributes: resolvedAttributes
            }
          }
        }
      });

      clearSignifydSessionId();

      return result;
    },
    [getOrderAttributes, signifydSessionId, clearSignifydSessionId, mutate, cartId]
  );

  return [handlePlaceOrder, { loading, error }] as const;
};
